import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, TrendingUp } from "lucide-react";
import { inr } from "@/lib/apiClient";
import { CATEGORY_LABELS } from "@/data/site";

// Editorial split row — alternating photography and text
export function PackageRow({ item, index = 0 }) {
  const flip = index % 2 === 1;
  return (
    <Link
      to={`/packages/${item.slug}`}
      data-testid={`package-row-${item.slug}`}
      className="group grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-12 items-center"
    >
      <div className={`img-zoom rounded-md overflow-hidden aspect-[4/3] ${flip ? "md:order-2" : ""}`}>
        <img src={item.hero_image} alt={item.title} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className={flip ? "md:order-1 md:pr-6" : "md:pl-6"}>
        <p className="eyebrow">{CATEGORY_LABELS[item.category] || item.category} · {item.region}</p>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mt-3 text-[#2A1808] group-hover:text-[#553204] transition-colors">
          {item.title}
        </h3>
        <p className="mt-3 text-base text-[#553204] italic font-display text-xl">{item.summary}</p>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#8A6B47]">
          <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {item.nights}N / {item.days}D</span>
          <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {item.destinations.slice(0, 3).join(" · ")}</span>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <span className="text-[#2A1808]"><span className="text-xs text-[#8A6B47]">Curated from </span><span className="font-display text-2xl font-semibold">{inr(item.price_from)}</span></span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#F49E1A]">
            Explore Journey <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// Compact card for grids
export function PackageCard({ item }) {
  return (
    <Link
      to={`/packages/${item.slug}`}
      data-testid={`package-card-${item.slug}`}
      className="group block rounded-md overflow-hidden bg-[#FFFBF5] border border-[#553204]/10 transition-shadow duration-300 hover:shadow-[0_20px_50px_-24px_rgba(42,24,8,0.4)]"
    >
      <div className="img-zoom aspect-[3/2] overflow-hidden relative">
        <img src={item.hero_image} alt={item.title} className="h-full w-full object-cover" loading="lazy" />
        {item.show_popularity && item.popularity > 0 && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#2A1808]/80 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold text-[#F49E1A]" data-testid={`popularity-${item.slug}`}>
            <TrendingUp size={12} /> Popularity {item.popularity}
          </span>
        )}
      </div>
      <div className="p-6">
        <p className="eyebrow">{CATEGORY_LABELS[item.category] || item.category} · {item.region}</p>
        <h3 className="font-display text-2xl font-medium mt-2 text-[#2A1808] leading-tight">{item.title}</h3>
        <p className="mt-2 text-sm text-[#8A6B47] line-clamp-2">{item.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-[#553204]/10 pt-4">
          <span className="text-xs text-[#8A6B47]">{item.nights}N / {item.days}D</span>
          <span className="text-[#2A1808]"><span className="text-xs text-[#8A6B47]">from </span><span className="font-display text-xl font-semibold">{inr(item.price_from)}</span></span>
        </div>
      </div>
    </Link>
  );
}
